<script setup lang="ts">
    import { computed, onMounted, watch } from 'vue'
    import { useStorage } from '@vueuse/core'
    import { SelectFieldSingle } from '@/js/components/Common'
    import { useKennwerteAccess } from '@/js/components/Common/useKennwerteAccess'
    import type { Fachbereich, KennzahlStudiengang } from '@/js/model'
    import { useKennzahlStudiengangStore, useStudiengangStore } from '@/js/stores'

    const props = defineProps<{
        selectedFachbereich: Fachbereich | null
    }>()

    const kennzahlStudiengangStore = useKennzahlStudiengangStore()
    const studiengangStore = useStudiengangStore()
    const { seesAllKennwerte, allowedStudiengangIds } = useKennwerteAccess()

    /**
     * Filtert die Kennzahl-Studiengänge passend zum gewählten Fachbereich.
     * Mapping läuft über studiengangId → StudiengangView (für die fachbereich-IRI),
     * weil KennzahlStudiengangView selbst die Fachbereich-IRI nicht denormalisiert.
     * Bei eingeschränktem Studiengang-Scope (SFB, QMB ohne isForAll) zusätzlich
     * auf die erlaubten Studiengang-IDs einschränken.
     */
    const kennzahlStudiengaenge = computed(() => {
        const fachbereichIri = props.selectedFachbereich?.['@id']
        if (!fachbereichIri) {
            return []
        }
        const studiengangIdsForFachbereich = new Set(
            studiengangStore.studiengaenge
                .filter((stg) => stg.fachbereich === fachbereichIri)
                .map((stg) => stg.studiengangId)
        )

        const restrictByStudiengang =
            !seesAllKennwerte.value && allowedStudiengangIds.value.length > 0
        const scopedStudiengangIds = new Set(allowedStudiengangIds.value)

        return kennzahlStudiengangStore.kennzahlStudiengaenge.filter((entry) => {
            if (!studiengangIdsForFachbereich.has(entry.studiengangId)) {
                return false
            }
            if (restrictByStudiengang && !scopedStudiengangIds.has(entry.studiengangId)) {
                return false
            }
            return true
        })
    })

    const emit = defineEmits(['kennzahlStudiengangSelected'])

    const kennzahlStudiengangSelectOptions = computed(() => [
        { value: 'default', title: 'Studiengang wählen ...', disabled: true },
        ...kennzahlStudiengaenge.value.map((entry) => ({
            title: entry.label,
            value: entry.kennzahlStudiengangId
        }))
    ])

    const selectedKennzahlStudiengang = computed<KennzahlStudiengang | null>(() => {
        if (!selectedKennzahlStudiengangId.value) {
            return null
        }

        return (
            kennzahlStudiengangStore.kennzahlStudiengaenge.find(
                (entry) => entry.kennzahlStudiengangId === selectedKennzahlStudiengangId.value
            ) ?? null
        )
    })

    const selectedKennzahlStudiengangId = useStorage(
        'lernleitstand-kennzahl-studiengang-select-on-dashboard',
        () =>
            kennzahlStudiengangSelectOptions.value.length > 0
                ? kennzahlStudiengangSelectOptions.value[0].value
                : 'default'
    )

    watch(
        () => props.selectedFachbereich,
        () => {
            const stillValid = kennzahlStudiengaenge.value.some(
                (entry) => entry.kennzahlStudiengangId === selectedKennzahlStudiengangId.value
            )
            if (stillValid) {
                return
            }
            if (!props.selectedFachbereich) {
                selectedKennzahlStudiengangId.value = null
            } else if (kennzahlStudiengangSelectOptions.value[1]?.value) {
                selectedKennzahlStudiengangId.value = kennzahlStudiengangSelectOptions.value[1].value
            } else {
                selectedKennzahlStudiengangId.value = kennzahlStudiengangSelectOptions.value[0].value
            }
        }
    )

    watch(
        () => selectedKennzahlStudiengangId.value,
        () => {
            emit('kennzahlStudiengangSelected', selectedKennzahlStudiengang.value)
        }
    )

    onMounted(async () => {
        emit('kennzahlStudiengangSelected', selectedKennzahlStudiengang.value)
    })
</script>

<template>
    <div v-if="kennzahlStudiengangStore.kennzahlStudiengaenge.length" class="d-flex align-center">
        <select-field-single
            v-model="selectedKennzahlStudiengangId"
            :items="kennzahlStudiengangSelectOptions"
            label="Studiengang"
            placeholder="Studiengang wählen"
            data-test="select-kennzahl-studiengang"
            min-width="300px"
            class="ml-5"
        />
    </div>
</template>

<style scoped></style>
